import React, { useState, useEffect } from 'react';
import {
  Typography,
  Toolbar,
  IconButton,
  TableHead,
  TableCell,
  Table as MuiTable,
  TablePagination,
  TableRow,
  TableBody,
  TableCellBaseProps,
  Checkbox,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { WithLoading } from 'src/components/common/WithLoading';
import Color from 'color';
import { orange } from '@mui/material/colors';

interface Props {
  onChange?: (params: { page: number; limit: number }) => any;
  onFetch?: () => any;
  onCancel?: any;
  loading: boolean;
  withPagination?: boolean;
  dense?: boolean;
  totalCount?: number | null;
  title?: string;
  onClickRow?: (item: any) => any;
  rowStyle?: (item: any) => any;
  headers: Array<{
    title: string;
    key?: string;
    align?: 'left' | 'right' | 'center';
    extract?: (item: any, index?: number) => React.ReactNode;
    component?: React.ElementType<TableCellBaseProps>;
    scope?: TableCellBaseProps['scope'];
    warning?: (item: any) => boolean;
    summary?: React.ReactNode;
    onClick?: (e: React.SyntheticEvent, item: any) => any;
  }>;
  data?: any[] | null;
  limit?: number | null;
  offset?: number | null;
  withSummary?: boolean;
  actions?: ((params: { selectedItems: any }) => React.ReactNode)[];
  extractId?: (item: any) => string | number;
}

type typeState = {
  limit: number;
  page: number;
};

export const Table: React.FunctionComponent<Props> = ({
  title,
  onFetch,
  withPagination = false,
  onChange,
  onCancel,
  loading,
  data = [],
  totalCount = 0,
  onClickRow,
  headers,
  actions,
  withSummary,
  dense,
  rowStyle,
  extractId,
  limit: providedLimit = 5,
  offset: providedOffset = 0,
}) => {
  const [state, changeState] = useState<typeState>({
    limit: 5,
    page: 0,
  });

  const [selectedItems, setSelectedItems] = useState<typeof data>([]);

  const setState = (data: Partial<typeState> = {}) => {
    const nextState = { ...state, ...data };

    if (onChange) {
      onChange(nextState);
    }

    changeState(nextState);
  };

  useEffect(() => {
    changeState({
      ...state,
      limit: providedLimit || 5,
      page:
        typeof providedOffset === 'number' && typeof providedLimit === 'number'
          ? providedOffset / providedLimit
          : 0,
    });
  }, [providedLimit, providedOffset]);

  return (
    <div>
      {Boolean(title || onFetch) && (
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flex: '1 1 100%',
            }}
          >
            {title}
          </Typography>

          {actions?.map((action, index) => (
            <React.Fragment key={index}>
              {action({ selectedItems })}
            </React.Fragment>
          ))}
          {Boolean(onFetch) && (
            <IconButton
              onClick={() => {
                if (onFetch) {
                  onFetch();
                }
              }}
            >
              <Refresh />
            </IconButton>
          )}
        </Toolbar>
      )}
      <WithLoading loading={loading} onCancel={onCancel}>
        <MuiTable aria-label="table" size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={theme => ({ color: theme.palette.grey[500] })}
                key="index"
              >
                #
              </TableCell>
              {headers.map(header => {
                if (!header.extract) return null;
                if (header.title === 'checkbox') {
                  return (
                    <TableCell key={header.key || header.title}>
                      <Checkbox
                        checked={Boolean(
                          data?.length === selectedItems?.length,
                        )}
                        onChange={() =>
                          data?.length !== selectedItems?.length
                            ? setSelectedItems(data)
                            : setSelectedItems([])
                        }
                      />
                    </TableCell>
                  );
                }
                return (
                  <TableCell
                    key={header.key || header.title}
                    align={header.align || 'left'}
                  >
                    {header.title}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((item: any, index) => (
              <TableRow
                hover
                key={extractId ? extractId(item) : item.id || item.code}
                onClick={e => (onClickRow ? onClickRow(item) : null)}
                style={rowStyle ? rowStyle(item) : null}
              >
                <TableCell
                  sx={theme => ({ color: theme.palette.grey[500] })}
                  key="index"
                >
                  {index + 1}
                </TableCell>
                {headers.map(header => {
                  if (!header.extract) return null;
                  if (header.title === 'checkbox') {
                    return (
                      <TableCell
                        key={header.key || header.title}
                        align={header.align || 'left'}
                      >
                        <Checkbox
                          checked={Boolean(
                            selectedItems?.find(
                              selectedItem => selectedItem.id === item.id,
                            ),
                          )}
                          onChange={() =>
                            selectedItems &&
                            selectedItems.find(
                              selectedItem => selectedItem.id === item.id,
                            )
                              ? setSelectedItems(
                                  selectedItems.filter(
                                    selectedItem => selectedItem.id !== item.id,
                                  ),
                                )
                              : selectedItems &&
                                setSelectedItems([...selectedItems, item])
                          }
                        />
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell
                      onClick={e =>
                        header.onClick ? header.onClick(e, item) : null
                      }
                      align={header.align || 'left'}
                      key={header.key || header.title}
                      component={header.component}
                      scope={header.scope}
                      sx={
                        header.warning && header.warning(item)
                          ? {
                              backgroundColor: Color(orange[500])
                                .alpha(0.1)
                                .string(),
                            }
                          : {}
                      }
                    >
                      {header.extract(item, index)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {withSummary && (
              <TableRow>
                <TableCell />
                {headers.map(header => (
                  <TableCell key={header.title} align={header.align || 'left'}>
                    {header.summary}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
        {withPagination && (
          <TablePagination
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} из ${count !== -1 ? count : `больше чем ${to}`}`
            }
            // labelRowsPerPage="элементов на странице"
            count={totalCount || 1}
            rowsPerPage={state.limit}
            page={state.page}
            rowsPerPageOptions={[]}
            onPageChange={(e, value) => setState({ page: value })}
          />
        )}
      </WithLoading>
    </div>
  );
};
